import React, { useRef, useState, useCallback } from 'react'
import { Box, FormControl, useMediaQuery } from '@mui/material'
import PropTypes from 'prop-types'
import { useController } from 'react-hook-form'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import { Editor } from '@tinymce/tinymce-react'
import DOMPurify from 'dompurify'

import { renderOnDivWithShortcodes } from '../text-editor/text-editor.utils'
import Loading from '../loading/loading.component'

import { styles } from './rich-content-editor-style'
import { getDefaultStyle, getShortcodeStyles } from './rich-content-editor-style'
import {
  PLUGIN_SETS,
  TOOLBAR_SETS,
  UI_STRINGS,
  SHORT_CODES_LIST,
  VALID_TAGS,
  MENU_CONFIG,
} from './rich-content-editor-constant'

const RichContentEditor = ({
  name,
  hasShortCode = false,
  shortCodes,
  options = [],
  height = 500,
  plugins = PLUGIN_SETS.URL_ONLY,
  toolbar = TOOLBAR_SETS.ESSENTIAL,
  menubar = UI_STRINGS.DEFAULT_MENUBAR,
  theme: editorTheme,
}) => {
  const { t } = useTranslation('common')
  const editorRef = useRef(null)
  const editorContainerRef = useRef(null)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [isLoading, setIsLoading] = useState(true)
  const [editorId] = useState(`editor-${Math.random().toString(36).substring(2, 9)}`)
  const { control } = useFormContext()
  const {
    field: { onChange, value, ...field },
  } = useController({ control, name })

  const shortCodesListToUse = shortCodes?.length ? shortCodes : SHORT_CODES_LIST

  const initialValue = value || ''

  const formatShortcodesForDisplay = (content) => {
    if (!content || !hasShortCode) return content

    let result = content
    shortCodesListToUse.forEach((code) => {
      const encodedCode = code.replace(/</g, '&lt;').replace(/>/g, '&gt;')
      const styledShortcode = `<span class="shortcode" contenteditable="false">${encodedCode}</span>`

      result = result.replace(
        new RegExp(code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
        styledShortcode
      )
      result = result.replace(
        new RegExp(encodedCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
        styledShortcode
      )
    })

    return result
  }

  const shortCodeOptions = options?.length
    ? options
    : [...(shortCodes ?? SHORT_CODES_LIST)].map((code) => ({
        label: code,
        value: code,
      }))

  const contentStyle = getDefaultStyle(theme)
  const shortcodeStyle = getShortcodeStyles(theme)
  const combinedContentStyle = `${contentStyle}\n${shortcodeStyle}`

  const getToolbar = () => {
    // Mobile-optimized toolbar with fewer buttons
    if (isMobile) {
      let mobileToolbar = 'bold italic | bullist numlist | link'
      if (hasShortCode && shortCodeOptions.length > 0) {
        mobileToolbar += ' | shortcodes'
      }
      return mobileToolbar
    }

    // Desktop toolbar
    if (hasShortCode && shortCodeOptions.length > 0) {
      return toolbar.includes('shortcodes') ? toolbar : `${toolbar} | shortcodes`
    }
    return toolbar.replace(' | shortcodes', '').replace('| shortcodes', '')
  }

  const handleEditorChange = (content) => {
    const hasShortcodes =
      hasShortCode &&
      (shortCodesListToUse.some((code) => content.includes(code)) ||
        content.includes('class="shortcode"'))
    const processed = hasShortcodes ? renderOnDivWithShortcodes(content) : content

    onChange(processed)
  }

  return (
    <FormControl fullWidth>
      <Box sx={styles.editorContainer} ref={editorContainerRef}>
        {isLoading && <Loading />}
        <Editor
          id={editorId}
          apiKey={UI_STRINGS.API_KEY}
          {...field}
          value={initialValue}
          onEditorChange={handleEditorChange}
          onInit={(_, editor) => {
            editorRef.current = editor
            setIsLoading(false)

            if (hasShortCode && value) {
              const formattedContent = formatShortcodesForDisplay(value)
              if (formattedContent !== value) {
                editor.setContent(formattedContent)
              }
            }
          }}
          init={{
            selector: `#${editorId}`,
            suffix: '.min',
            height: isMobile ? Math.min(height, 150) : height, // Smaller height on mobile
            menubar: isMobile ? false : menubar, // Hide menubar on mobile
            menu: isMobile ? {} : MENU_CONFIG, // Disable menu on mobile
            statusbar: false,
            branding: false,
            onboarding: false,
            entity_encoding: 'raw',
            plugins,
            toolbar: getToolbar(),
            toolbar_mode: isMobile ? 'sliding' : 'wrap', // Sliding toolbar on mobile
            toolbar_sticky: false,
            toolbar_location: 'top',
            // Mobile-specific optimizations
            mobile: {
              theme: 'mobile',
              toolbar_mode: 'scrolling',
              menubar: false,
            },
            image_advtab: true,
            image_uploadtab: false,
            image_caption: false,
            images_upload_handler: undefined,
            automatic_uploads: false,
            image_description: false,
            convert_urls: false,
            relative_urls: false,
            remove_script_host: false,
            image_dimensions: true,
            image_title: false,
            image_prepend_url: '',
            content_style: combinedContentStyle,
            skin: editorTheme === 'dark' ? 'oxide-dark' : 'oxide',
            content_css: editorTheme === 'dark' ? 'dark' : 'default',
            valid_elements: VALID_TAGS.VALID_ELEMENTS,
            extended_valid_elements: VALID_TAGS.EXTENDED_VALID_ELEMENTS,
            verify_html: false,
            cleanup: false,
            forced_root_block: false,
            allow_unsafe_link_target: true,
            promotion: false,
            paste_data_images: false,
            paste_filter_drop: true,
            paste_preprocess: (_, args) => {
              args.content = DOMPurify.sanitize(args.content.replace(/<img[^>]*>/g, ''))
            },
            setup: (editor) => {
              editor.options.set('file_picker_callback', undefined)

              if (hasShortCode) {
                editor.ui.registry.addMenuButton('shortcodes', {
                  text: t('editor.useShortcode'),
                  tooltip: t('editor.insertShortcode'),
                  fetch: (callback) => {
                    const items = shortCodeOptions.map((option) => ({
                      type: 'menuitem',
                      text: option.label,
                      onAction: () => {
                        editor.focus()
                        const encodedValue = option.value
                          .replace(/</g, '&lt;')
                          .replace(/>/g, '&gt;')
                        const html = `<span class="shortcode" contenteditable="false">${encodedValue}</span>&nbsp;`
                        editor.insertContent(html)
                      },
                    }))
                    callback(items)
                  },
                })
              }
            },
          }}
        />
      </Box>
    </FormControl>
  )
}

RichContentEditor.propTypes = {
  name: PropTypes.string.isRequired,
  hasShortCode: PropTypes.bool,
  shortCodes: PropTypes.array,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ),
  height: PropTypes.number,
  plugins: PropTypes.array,
  toolbar: PropTypes.string,
  menubar: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  theme: PropTypes.string,
}

RichContentEditor.defaultProps = {
  hasShortCode: false,
  options: [],
  height: 500,
  plugins: PLUGIN_SETS.URL_ONLY,
  toolbar: TOOLBAR_SETS.ESSENTIAL,
  menubar: UI_STRINGS.DEFAULT_MENUBAR,
  theme: 'light',
}

export default RichContentEditor
